import React, { FunctionComponent, useState } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { medicationsSidePanelMessages } from './MedicationsSidePanel.messages'
import { BodyText, Size as BodyTextSize } from '../../../atoms/bodyText/BodyText'
import { InfoIcon } from '../../../atoms/icons/InfoIcon'
import { Tooltip } from '../../../atoms/tooltip/Tooltip'
import { tID } from '../../../utils/utils'
import { ClientDetailsSidePanelSection } from '../ClientDetailsSidePanelSection'

const COLLAPSED_MEDICATION_LIMIT = 3

export type MedicationSidePanelItem = {
  id: string
  name: string
  prescribedOn: string
  quantity: string
  refillCount: number
  directions?: string
}

export type MedicationsSidePanelSectionProps = {
  medications?: readonly MedicationSidePanelItem[]
  defaultExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

const CollapsedMedicationList = styled.View(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing['8px'],
}))

const CollapsedMedicationNames = styled.View(({ theme }) => ({
  width: '100%',
  gap: theme.spacing['4px'],
}))

const CollapsedMedicationName = styled(BodyText)({
  width: '100%',
})

const MoreMedications = styled(BodyText)(({ theme }) => ({
  marginTop: theme.spacing['4px'],
}))

const MedicationList = styled.View(({ theme }) => ({
  width: '100%',
  gap: theme.spacing['16px'],
}))

const MedicationItem = styled.View({
  width: '100%',
})

const MedicationDate = styled(BodyText)(({ theme }) => ({
  marginTop: theme.spacing['4px'],
}))

const MedicationDetails = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: theme.spacing['2px'],
}))

const DirectionsTooltipAnchor = styled.View(({ theme }) => ({
  width: 16,
  height: 16,
  marginLeft: theme.spacing['8px'],
}))

export const MedicationsSidePanelSection: FunctionComponent<MedicationsSidePanelSectionProps> = ({
  medications,
  defaultExpanded = false,
  onExpandedChange,
}) => {
  const { formatMessage, formatDate } = useIntl()
  const { colors } = useTheme()
  const [expanded, setExpanded] = useState(defaultExpanded)

  if (!medications?.length) {
    return null
  }

  const medicationCount = medications.length
  const collapsedMedications = medications.slice(0, COLLAPSED_MEDICATION_LIMIT)
  const hiddenMedicationCount = medicationCount - collapsedMedications.length
  const subtitle = formatMessage(
    expanded ? medicationsSidePanelMessages.subtitleWithCount : medicationsSidePanelMessages.subtitle,
    { count: medicationCount },
  )

  const handleExpandedChange = (nextExpanded: boolean) => {
    setExpanded(nextExpanded)
    onExpandedChange?.(nextExpanded)
  }

  const collapsedTitle = (
    <CollapsedMedicationList>
      <CollapsedMedicationNames>
        {collapsedMedications.map((medication) => (
          <CollapsedMedicationName
            key={medication.id}
            testID={tID(`Medications-collapsed-item-${medication.id}`)}
            size={BodyTextSize.DEFAULT}
            color={colors.textPrimary}
            text={medication.name}
            wrap
            regular
          />
        ))}
      </CollapsedMedicationNames>
      {hiddenMedicationCount > 0 && (
        <MoreMedications
          testID={tID('Medications-collapsed-more')}
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          text={formatMessage(medicationsSidePanelMessages.moreMedications, { count: hiddenMedicationCount })}
          regular
        />
      )}
    </CollapsedMedicationList>
  )

  const content = (
    <MedicationList testID={tID('Medications-list')}>
      {medications.map((medication) => {
        const directions = medication.directions?.trim()

        return (
          <MedicationItem key={medication.id} testID={tID(`Medications-item-${medication.id}`)}>
            <BodyText
              testID={tID(`Medications-item-${medication.id}-name`)}
              size={BodyTextSize.DEFAULT}
              color={colors.textPrimary}
              text={medication.name}
              wrap
              regular
            />
            <MedicationDate
              testID={tID(`Medications-item-${medication.id}-prescribed-on`)}
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
              text={formatMessage(medicationsSidePanelMessages.prescribedOn, {
                date: formatDate(medication.prescribedOn, { year: 'numeric', month: 'short', day: 'numeric' }),
              })}
              regular
            />
            <MedicationDetails>
              <BodyText
                testID={tID(`Medications-item-${medication.id}-quantity`)}
                size={BodyTextSize.SMALL}
                color={colors.textSecondary}
                text={formatMessage(medicationsSidePanelMessages.quantityAndRefills, {
                  quantity: medication.quantity,
                  refillCount: medication.refillCount,
                })}
                regular
              />
              {directions && (
                <DirectionsTooltipAnchor>
                  <Tooltip
                    testID={tID(`Medications-item-${medication.id}-directions`)}
                    placement='top'
                    content={directions}
                    accessibilityLabel={formatMessage(medicationsSidePanelMessages.directionsAccessibilityLabel, {
                      medicationName: medication.name,
                    })}
                    hoverEnabled
                    openOnPress
                  >
                    <InfoIcon size={16} />
                  </Tooltip>
                </DirectionsTooltipAnchor>
              )}
            </MedicationDetails>
          </MedicationItem>
        )
      })}
    </MedicationList>
  )

  return (
    <ClientDetailsSidePanelSection
      subtitle={subtitle}
      accessibilityName={formatMessage(medicationsSidePanelMessages.subtitle)}
      header={{
        kind: 'collapsible',
        collapsedTitle,
        collapsedTitleIsLayout: true,
        expanded,
        onExpandedChange: handleExpandedChange,
      }}
      content={content}
      testIDPrefix='Medications'
    />
  )
}
